import React, { useEffect, useMemo, useState } from 'react';
import { Clock, Users, Wifi, WifiOff, X } from 'lucide-react';
import { presenceService, type PresenceData } from '../../services/presenceService';
import { usersService } from '../../services/usersService';
import type { User } from '../../types';
import UserAvatar from '../UserAvatar';

interface OrganizationMembersWidgetProps {
  orgId: string;
  currentUserId: string;
}

interface MemberRow {
  user: User;
  presence?: PresenceData;
}

const roleLabel: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  sales_rep: 'Sales',
};

function getPresenceTime(value?: number): number {
  return typeof value === 'number' ? value : 0;
}

function isRowOnline(row: MemberRow): boolean {
  return row.presence ? presenceService.isUserOnline(getPresenceTime(row.presence.lastSeen)) : false;
}

const OrganizationMembersWidget: React.FC<OrganizationMembersWidgetProps> = ({
  orgId,
  currentUserId,
}) => {
  const [members, setMembers] = useState<User[]>([]);
  const [presence, setPresence] = useState<PresenceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadMembers = async () => {
      setLoading(true);
      try {
        const users = await usersService.getUsers(orgId);
        if (mounted) {
          setMembers(users.filter(user => user.status !== 'inactive'));
        }
      } catch (error) {
        console.error('Failed to load organization members:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void loadMembers();
    const unsubscribe = presenceService.onPresenceChange(orgId, setPresence);

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [orgId]);

  const rows = useMemo<MemberRow[]>(() => {
    const presenceByUser = new Map(presence.map(item => [item.userId, item]));
    return members
      .map(user => ({
        user,
        presence: presenceByUser.get(user.id),
      }))
      .sort((a, b) => {
        const aOnline = a.presence ? presenceService.isUserOnline(getPresenceTime(a.presence.lastSeen)) : false;
        const bOnline = b.presence ? presenceService.isUserOnline(getPresenceTime(b.presence.lastSeen)) : false;
        if (aOnline !== bOnline) return aOnline ? -1 : 1;
        return getPresenceTime(b.presence?.lastSeen) - getPresenceTime(a.presence?.lastSeen);
      });
  }, [members, presence]);

  const onlineRows = rows.filter(isRowOnline);
  const onlineCount = onlineRows.length;
  const visibleRows = onlineRows.slice(0, 5);
  const hiddenCount = Math.max(0, onlineRows.length - visibleRows.length);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(value => !value)}
        className="flex items-center gap-2 rounded-full border border-subtle bg-bg-card px-2.5 py-1.5 shadow-card hover:border-strong hover:bg-bg-surface transition-colors"
        title="Online-Mitglieder anzeigen"
      >
        <div className="flex -space-x-2">
          {loading ? (
            <>
              <span className="h-8 w-8 rounded-full border-2 border-bg-card bg-bg-surface animate-pulse" />
              <span className="h-8 w-8 rounded-full border-2 border-bg-card bg-bg-surface animate-pulse" />
            </>
          ) : visibleRows.length > 0 ? (
            visibleRows.map(({ user, presence: userPresence }) => {
              const lastSeen = getPresenceTime(userPresence?.lastSeen);
              const displayName = user.name || user.email || userPresence?.displayName || 'User';

              return (
                <span key={user.id} className="relative inline-flex">
                  <UserAvatar
                    photoURL={user.avatar || userPresence?.avatar || undefined}
                    name={displayName}
                    size="sm"
                    className="border-2 border-bg-card"
                  />
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-bg-card bg-success" />
                </span>
              );
            })
          ) : (
            <span className="grid h-8 w-8 place-items-center rounded-full border-2 border-bg-card bg-bg-surface text-txt-tertiary">
              <Users className="h-4 w-4" />
            </span>
          )}
          {hiddenCount > 0 && (
            <span className="grid h-8 w-8 place-items-center rounded-full border-2 border-bg-card bg-bg-surface text-[11px] font-semibold text-txt-secondary">
              +{hiddenCount}
            </span>
          )}
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-txt-secondary">
          <span className="h-2 w-2 rounded-full bg-success" />
          {onlineCount} online
        </span>
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-3 w-[min(92vw,420px)] overflow-hidden rounded-xl border border-default bg-bg-card shadow-heavy">
          <div className="flex items-center justify-between border-b border-subtle px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold text-txt">Organisation</h3>
              <p className="text-xs text-txt-tertiary">{rows.length} Mitglieder · {onlineCount} online</p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="grid h-8 w-8 place-items-center rounded-full text-txt-tertiary hover:bg-bg-surface hover:text-txt"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto divide-y divide-subtle">
            {loading ? (
              <div className="p-4 space-y-3">
                {[0, 1, 2].map(i => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-bg-surface animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-1/3 rounded bg-bg-surface animate-pulse" />
                      <div className="h-2.5 w-1/2 rounded bg-bg-surface animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-sm text-txt-tertiary text-center">
                Keine Mitglieder gefunden.
              </div>
            ) : (
              rows.map(({ user, presence: userPresence }) => {
                const lastSeen = getPresenceTime(userPresence?.lastSeen);
                const isOnline = userPresence ? presenceService.isUserOnline(lastSeen) : false;
                const displayName = user.name || user.email || userPresence?.displayName || 'User';

                return (
                  <div key={user.id} className="px-4 py-3 flex items-center gap-3 hover:bg-bg-surface transition-colors">
                    <div className="relative">
                      <UserAvatar
                        photoURL={user.avatar || userPresence?.avatar || undefined}
                        name={displayName}
                        size="sm"
                      />
                      <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-bg-card ${isOnline ? 'bg-success' : 'bg-txt-muted'}`} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-medium text-txt truncate">
                          {displayName}
                          {user.id === currentUserId && <span className="text-txt-tertiary font-normal"> · Du</span>}
                        </p>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-surface text-txt-tertiary border border-subtle">
                          {roleLabel[user.role] || user.role || 'Member'}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-txt-tertiary">
                        {isOnline ? (
                          <Wifi className="w-3 h-3 text-success" />
                        ) : (
                          <WifiOff className="w-3 h-3 text-txt-muted" />
                        )}
                        <span>{lastSeen ? presenceService.formatLastSeen(lastSeen) : 'Noch keine Aktivität'}</span>
                        {userPresence?.currentPage && (
                          <>
                            <span>·</span>
                            <span className="truncate">{userPresence.currentPage}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <Clock className="w-4 h-4 text-txt-muted flex-shrink-0" />
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {isOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default"
          aria-label="Mitgliederstatus schließen"
          onClick={() => setIsOpen(false)}
          tabIndex={-1}
        />
      )}
    </div>
  );
};

export default OrganizationMembersWidget;
